import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import ConversationPanel from './ConversationPanel';
import PromptInputBar from './PromptInputBar';
import { ProjectContext } from '../../contexts/ProjectContext';
import useNotification from '../../hooks/useNotification';
import conversationService from '../../services/conversationService';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8003/api').replace(/\/api\/?$/, '');

/**
 * Utility function to strip markdown code fences from content
 *
 * Handles cases like:
 * - ```json\n{"content": "..."}\n```
 * - {"content": "{\n  \"content\": \"...\"\n}"}
 * - Plain markdown content
 *
 * @param {string} content - The content to clean
 * @returns {string} - Cleaned content without code fences
 */
const stripCodeFences = (content) => {
  if (!content || typeof content !== 'string') {
    console.log('Frontend: Empty or invalid content');
    return '';
  }

  let cleaned = content.trim();
  console.log(`Frontend 🔍: Input length=${cleaned.length}, starts with:`, cleaned.substring(0, 50));

  // Remove markdown code fences if present
  if (cleaned.startsWith('```')) {
    console.log('Frontend 📝: Detected markdown code fences, removing...');
    cleaned = cleaned.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '').trim();
    console.log(`Frontend ✂️: After fence removal, length=${cleaned.length}`);
  }

  // Try to parse as JSON and extract nested content
  try {
    const parsed = JSON.parse(cleaned);
    console.log('Frontend ✅: Successfully parsed outer JSON, keys:', Object.keys(parsed));

    if (parsed.content && typeof parsed.content === 'string') {
      console.log(`Frontend 🔎: Found 'content' field (length=${parsed.content.length}), checking if nested...`);

      // Try to parse nested JSON (this automatically unescapes \n)
      try {
        const innerParsed = JSON.parse(parsed.content);
        console.log('Frontend ✅: Successfully parsed inner JSON, keys:', Object.keys(innerParsed));

        if (innerParsed.content && typeof innerParsed.content === 'string') {
          console.log(`Frontend ✨: Extracted final content (length=${innerParsed.content.length})`);
          cleaned = innerParsed.content;
        } else {
          console.log('Frontend 📄: Using inner parsed content as-is');
          cleaned = parsed.content;
        }
      } catch {
        // Not nested JSON, use the string as-is (already unescaped)
        console.log(`Frontend 📄: Content is not nested JSON, using string as-is (length=${parsed.content.length})`);
        cleaned = parsed.content;
      }
    } else if (parsed.content) {
      console.log('Frontend 📄: Using content field directly');
      cleaned = parsed.content;
    }

    console.log(`Frontend ✅: Final cleaned content length=${cleaned.length}`);
  } catch (e) {
    // Not JSON, use cleaned content as-is
    console.log('Frontend 📄: Content is not JSON, using as plain text:', e.message);
  }

  return cleaned;
};

const ProjectInfoAccordion = ({ projectInfo, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState({});
  const [editContent, setEditContent] = useState({});
  const { showNotification } = useNotification();

  const sections = [
    { key: 'executiveSummary', title: 'Executive Summary', icon: '📋' },
    { key: 'projectVision', title: 'Project Vision', icon: '🎯' },
    { key: 'businessCase', title: 'Business Case', icon: '💼' },
    { key: 'targetAudience', title: 'Target Audience', icon: '👥' },
    { key: 'competitiveAnalysis', title: 'Competitive Analysis', icon: '📊' },
    { key: 'successMetrics', title: 'Success Metrics & KPIs', icon: '📈' },
    { key: 'projectScope', title: 'Project Scope', icon: '🔍' },
    { key: 'constraintsAssumptions', title: 'Constraints & Assumptions', icon: '⚠️' },
    { key: 'timelineMilestones', title: 'Timeline & Milestones', icon: '📅' },
    { key: 'riskAssessment', title: 'Risk Assessment', icon: '🚨' }
  ];

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    showNotification('success', 'Content copied to clipboard!');
  };

  const handleEdit = (key) => {
    setEditMode({ ...editMode, [key]: true });
    setEditContent({ ...editContent, [key]: projectInfo[key] || '' });
  };

  const handleSave = async (key) => {
    try {
      await onUpdate(key, editContent[key]);
      setEditMode({ ...editMode, [key]: false });
      showNotification('success', 'Section updated successfully!');
    } catch (error) {
      showNotification('error', 'Failed to update section');
    }
  };

  const handleCancel = (key) => {
    setEditMode({ ...editMode, [key]: false });
    setEditContent({ ...editContent, [key]: projectInfo[key] || '' });
  };

  const parseContent = (content) => {
    if (!content) return [];
    
    // Split content into paragraphs and lists
    const lines = content.split('\n').filter(line => line.trim());
    const parsed = [];
    let currentList = null;

    lines.forEach((line) => {
      if (line.match(/^[\d\-\*]\./)) {
        // List item
        if (!currentList) {
          currentList = { type: 'list', items: [] };
          parsed.push(currentList);
        }
        currentList.items.push(line.replace(/^[\d\-\*]\.?\s*/, ''));
      } else if (line.includes(':') && line.indexOf(':') < 50) {
        // Key-value pair
        if (currentList) currentList = null;
        const [key, ...valueParts] = line.split(':');
        parsed.push({
          type: 'keyValue',
          key: key.trim(),
          value: valueParts.join(':').trim()
        });
      } else {
        // Regular paragraph
        if (currentList) currentList = null;
        parsed.push({ type: 'paragraph', text: line });
      }
    });

    return parsed;
  };

  const renderContent = (content) => {
    const parsed = parseContent(content);
    
    return parsed.map((item, index) => {
      if (item.type === 'list') {
        return (
          <Box key={index} sx={{ mb: 2 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {item.items.map((listItem, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>
                  <Typography variant="body2">{listItem}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        );
      } else if (item.type === 'keyValue') {
        return (
          <Box key={index} sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>{item.key}:</strong> {item.value}
            </Typography>
          </Box>
        );
      } else {
        return (
          <Typography key={index} variant="body2" sx={{ mb: 1 }}>
            {item.text}
          </Typography>
        );
      }
    });
  };

  return (
    <Box>
      {sections.map((section) => {
        const content = projectInfo[section.key];
        const isEditing = editMode[section.key];

        return (
          <Accordion
            key={section.key}
            expanded={expanded === section.key}
            onChange={handleChange(section.key)}
            sx={{
              mb: 1,
              '&:before': { display: 'none' },
              boxShadow: 1,
              '&.Mui-expanded': {
                margin: '8px 0',
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <span style={{ marginRight: 8 }}>{section.icon}</span>
                  {section.title}
                </Typography>
                {content && (
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon />}
                    label="Completed"
                    color="success"
                    sx={{ mr: 1 }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: 'background.default' }}>
              {content ? (
                <>
                  {isEditing ? (
                    <Box>
                      <textarea
                        value={editContent[section.key]}
                        onChange={(e) => setEditContent({
                          ...editContent,
                          [section.key]: e.target.value
                        })}
                        style={{
                          width: '100%',
                          minHeight: 200,
                          padding: 12,
                          borderRadius: 4,
                          border: '1px solid #ddd',
                          fontFamily: 'inherit',
                          fontSize: 14,
                          resize: 'vertical'
                        }}
                      />
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSave(section.key)}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancel(section.key)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {renderContent(content)}
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(section.key)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(content)}
                          sx={{ color: 'primary.main' }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This section will be populated after the project information is generated.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

const SplitViewProjectInfo = () => {
  const { projectId } = useParams();
  const { project, setProject } = useContext(ProjectContext);
  const { showNotification } = useNotification();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectInfo, setProjectInfo] = useState({});
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    if (project?.id) {
      loadConversations();
      loadProjectInfo();
    }
  }, [project]);

  const loadConversations = async () => {
    try {
      const convs = await conversationService.getConversations(project.id);
      setConversations(convs);
      
      // Find the project-info conversation or get the latest one
      const projectInfoConv = convs.find(c => c.task_id === 'project-info' || c.taskId === 'project-info');
      const conversationToLoad = projectInfoConv || convs[convs.length - 1];
      
      if (conversationToLoad) {
        setConversationId(conversationToLoad.id || conversationToLoad._id);
        loadConversation(conversationToLoad.id || conversationToLoad._id);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadConversation = async (convId) => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/conversations/${convId}`);
      if (response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const loadProjectInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/projects/${project.id}/info`);

      // Strip code fences before validation
      const rawContent = response.data?.content;
      const cleanedContent = stripCodeFences(rawContent);

      console.log('loadProjectInfo: Raw content length:', rawContent?.length);
      console.log('loadProjectInfo: Cleaned content length:', cleanedContent?.length);

      // Check if we have valid content - be less strict to persist panel after refresh
      // Only filter out truly empty content or explicit error messages
      const hasContent = cleanedContent && cleanedContent.trim().length > 0;
      const isErrorMessage = cleanedContent && (
        cleanedContent.toLowerCase().includes('no meaningful project information found') ||
        cleanedContent.toLowerCase().includes('awaiting user response')
      );

      if (hasContent && !isErrorMessage) {
        // Store the cleaned content - this will show the right panel
        setProjectInfo({ ...response.data, content: cleanedContent });
        console.log('✅ Project info loaded successfully, right panel will be visible');
      } else {
        // Only clear if truly empty or error message
        setProjectInfo({});
        console.log('⚠️ No valid project info found, right panel will be hidden');
      }
    } catch (error) {
      console.error('Error loading project info:', error);
      // On error (like 404), hide the right panel
      setProjectInfo({});
    }
  };

  const handleGenerateProjectInfo = async () => {
    if (!conversationId) {
      showNotification('error', 'No conversation found. Please start a conversation first.');
      return;
    }

    // Validate that there's meaningful content in the conversation
    if (!messages || messages.length === 0) {
      showNotification('warning', 'Please provide project information through the conversation before generating the document.');
      return;
    }

    // Check if there's any substantive content (from user or imported)
    const allContent = messages.map(msg => msg.content || '').join(' ');
    const hasSubstantiveContent = allContent.trim().length > 50;

    if (!hasSubstantiveContent) {
      showNotification('warning', 'Please provide project information through the conversation before generating the document.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_BASE_URL}/api/projects/${project.id}/generate-info`,
        { conversationId }
      );

      if (response.data) {
        setProjectInfo(response.data);
        showNotification('success', 'Project information generated successfully!');
      }
    } catch (error) {
      console.error('Error generating project info:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate project information';
      showNotification('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSection = async (sectionKey, content) => {
    try {
      const response = await axios.patch(
        `${BACKEND_BASE_URL}/api/projects/${project.id}/info`,
        { [sectionKey]: content }
      );

      if (response.data) {
        setProjectInfo({ ...projectInfo, [sectionKey]: content });
      }
    } catch (error) {
      console.error('Error updating section:', error);
      throw error;
    }
  };

  const handleSendMessage = async (content, attachments = []) => {
    // Project info is view-only, no chat functionality needed
    showNotification('info', 'Project information is generated from conversation history');
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', gap: 2, p: 2 }}>
      {/* Left Panel - Conversation History */}
      <Box sx={{
        flex: Object.keys(projectInfo).length > 0 ? 1 : 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: Object.keys(projectInfo).length > 0 ? '50%' : '100%',
        transition: 'max-width 0.3s ease'
      }}>
        <Paper sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Conversation History</Typography>
              <Typography variant="body2" color="text.secondary">
                Review the conversation that will be used to generate project information
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleGenerateProjectInfo}
              disabled={isLoading || !conversationId}
              startIcon={isLoading ? <CircularProgress size={20} /> : <InfoIcon />}
            >
              {isLoading ? 'Generating...' : 'Generate Project Info'}
            </Button>
          </Box>
        </Paper>
        <ConversationPanel
          messages={messages}
          isLoading={false}
          readOnly={true}
        />
      </Box>

      {/* Right Panel - Project Information (only shown when project info exists) */}
      {Object.keys(projectInfo).length > 0 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ mb: 2, p: 2 }}>
            <Typography variant="h6">Project Information Document</Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive project details generated from conversation
            </Typography>
          </Paper>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <ProjectInfoAccordion
              projectInfo={projectInfo}
              onUpdate={handleUpdateSection}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SplitViewProjectInfo;